import * as React from 'react'
import { ReportType } from '@/app/constants/reportType'
import { OutputType } from '@/app/constants/outputType'
import { getReportTypeByNumber } from '@/app/services/printout/reportTypeResolver'
import usePrintOutService from '../services/PrintOutService'

export type ReportCapability = {
  hasPdf: boolean
  hasCsv: boolean
  hasXlsx: boolean
}

export type ReportCapabilityMap = ReadonlyMap<ReportType, ReportCapability>

/**
 * Czy raport da się wygenerować w wybranym formacie.
 *
 * Odwzorowuje decyzję generatora (GenerateReportJob): raport, który ma XLSX i nie ma PDF,
 * zawsze wychodzi jako XLSX niezależnie od wyboru użytkownika, więc nigdy go nie gasimy.
 * Brak wpisu w mapie znaczy „nie wiem" (raport bez wpisu w fabryce, albo capabilities
 * jeszcze nie doszły) — wtedy nie blokujemy, bo generator i tak odrzuci zły format
 * z kodem EMPTY_DOCUMENT_FORMAT_NOT_SUPPORTED.
 */
export const supportsOutputFormat = (
  capabilities: ReportCapabilityMap,
  reportType: ReportType,
  outputFormat: OutputType,
): boolean => {
  const capability = capabilities.get(reportType)
  if (!capability) {
    return true
  }

  if (capability.hasXlsx && !capability.hasPdf) {
    return true
  }

  return outputFormat === OutputType.Csv ? capability.hasCsv : capability.hasPdf
}

const EMPTY_CAPABILITIES: ReportCapabilityMap = new Map()

/**
 * Pobiera z API listę formatów obsługiwanych przez każdy raport.
 *
 * Zastępuje ręcznie utrzymywaną tabelkę w kliencie — źródłem prawdy są flagi modelu
 * dokumentu na serwerze, dokładnie tak jak w WPF (PrintPreviewViewModel.CanGenerateCsv).
 */
export const useReportCapabilities = (companyId: string) => {
  const { getReportCapabilities } = usePrintOutService()
  const [capabilities, setCapabilities] =
    React.useState<ReportCapabilityMap>(EMPTY_CAPABILITIES)

  React.useEffect(() => {
    if (!companyId) {
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        const response = await getReportCapabilities({ companyId })
        if (cancelled) return

        const map = new Map<ReportType, ReportCapability>()
        response.forEach((entry) => {
          const reportType = getReportTypeByNumber(entry.reportType)
          if (reportType === ReportType.NONE) {
            return
          }
          map.set(reportType, {
            hasPdf: entry.hasPdf,
            hasCsv: entry.hasCsv,
            hasXlsx: entry.hasXlsx,
          })
        })
        setCapabilities(map)
      } catch (error) {
        // Bez capabilities nie gasimy niczego - lepiej pokazac raport i dostac
        // czytelny blad z generatora niz schowac dzialajaca pozycje.
        console.error('Błąd podczas pobierania formatów raportów:', error)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [companyId])

  return capabilities
}
